<template>
    <div class="absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words w-full body mt-3 text-transparent" aria-hidden="true">
        <template v-for="(segment, i) in segments" :key="i">
            <mark v-if="segment.type === 'warn'" class="bg-[#F6E9FA] rounded-sm text-transparent">{{ segment.text }}</mark>
            <mark v-else-if="segment.type === 'error'" class="bg-[#FEE2E2] rounded-sm text-transparent">{{ segment.text }}</mark>
            <template v-else>{{ segment.text }}</template>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { Span } from "~vue/utils/pii/keywordClassifier";
import { computed } from "vue";

interface Segment {
    text: string;
    type: "plain" | "warn" | "error";
}

const props = defineProps<{
    text: string;
    warnSpans: Span[];
    errorSpans: Span[];
}>();

const segments = computed<Segment[]>(() => {
    if (props.warnSpans.length === 0 && props.errorSpans.length === 0) {
        return [{ text: props.text, type: "plain" }];
    }

    // Merge and sort all spans, with error taking priority over warn
    type Tagged = { start: number; end: number; type: "warn" | "error" };
    const tagged: Tagged[] = [
        ...props.warnSpans.map<Tagged>((s) => ({ start: s.start, end: s.end, type: "warn" })),
        ...props.errorSpans.map<Tagged>((s) => ({ start: s.start, end: s.end, type: "error" })),
    ];
    tagged.sort((a, b) => a.start - b.start || (a.type === "error" ? -1 : 1));

    const result: Segment[] = [];
    let cursor = 0;

    for (const span of tagged) {
        if (span.start < cursor) {
            continue;
        } // skip overlapping spans
        if (span.start > cursor) {
            result.push({ text: props.text.slice(cursor, span.start), type: "plain" });
        }
        result.push({ text: props.text.slice(span.start, span.end), type: span.type });
        cursor = span.end;
    }

    if (cursor < props.text.length) {
        result.push({ text: props.text.slice(cursor), type: "plain" });
    }

    return result;
});
</script>
